'use client';

import React from "react";
import Image from "next/image";
import Button from "../../../ui/Button";
import WelcomeDashboard from "../desktop/WelcomeDashboard";
import { useAuthUser, logout } from "@/lib/auth";
// use string paths for images
const IconClose = "/assets/icons/icon-close.svg";
const BackArrow = "/assets/icons/back-arrow.svg";
const DrawerLinkArrow = "/assets/icons/drawer-link-arrow.svg";

import type { SubDrawer as SubDrawerCfg } from "../navbarData";
import Link from "next/dist/client/link";

type Props = { config: SubDrawerCfg | undefined; isOpen: boolean; onBack: () => void; onClose: () => void; onOpenSubDrawer: (id: string) => void };

export default function SubDrawer({ config, isOpen, onBack, onClose, onOpenSubDrawer }: Props) {
  const user = useAuthUser();

  if (!config) return null;

  return (
    <div
      className={`navbar__subdrawer fixed top-0 right-0 bottom-0 w-full z-[90] flex flex-col shadow-[-18px_0_42px_rgba(12,33,64,0.18)] transition-transform duration-[280ms] ease-in-out md:pb-0 pb-1 ${isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      aria-hidden={!isOpen}
      style={{ backgroundColor: config.bgColor ?? (config.isAccount ? '#FFE4B4' : '#ffffff') }}
    >
      <div className="navbar__drawer-header flex flex-col items-center gap-[17px] md:p-[24px_20px_8px_20px] p-[18px_16px_8px_16px]">
        <Link href="/" className="w-full flex items-center justify-center" aria-label="Retour à l'accueil">
          <Image
            className="navbar__drawer-logo w-64.5 h-fit object-contain"
            src="/assets/logo-blue.svg"
            alt="Spot Formations"
            title="Spot Formations"
            width={258}
            height={17.49}
          />
        </Link>

        <div className="navbar__drawer-meta relative w-full flex items-center justify-center">
          <button
            className="navbar__drawer-back absolute left-0 bg-transparent border-none text-[22px] text-[#1b365e] cursor-pointer"
            onClick={onBack}
            aria-label="Retour au menu"
            type="button"
          >
            <Image
              src={BackArrow}
              alt="Retour"
              title="Retour"
              width={24}
              height={24}
              draggable="false"
              className="w-[20px] h-[20px] object-contain block pointer-events-none"
            />
          </button>

          <h2 className={`${config.isAccount ? "text-[#FC7F16] font-bold" : "text-[#1b365e] font-light"} navbar__drawer-title m-0 text-[18px] font-red-hat leading-5 tracking-[2%]`}>
            {config.isAccount && user ? `Bienvenue ${user.firstName}` : config.title}
          </h2>

          <button
            className="navbar__drawer-close absolute right-0 bg-transparent border-none text-[22px] text-[#1b365e] cursor-pointer"
            onClick={onClose}
            aria-label="Fermer"
            type="button"
          >
            <Image
              src={IconClose}
              width={20}
              height={20}
              alt="Fermer"
              title="Fermer"
              draggable="false"
              className="w-[20px] h-[20px] object-contain block pointer-events-none"
            />
          </button>
        </div>
      </div>

      <div className={`navbar__subdrawer-body flex-1 ${config.isAccount ? "px-1" : "md:px-5 px-4"} py-6.5 flex flex-col gap-6 overflow-y-auto`}>
        {config.isAccount ? (
          user ? (
            <div className="w-full h-full">
              <WelcomeDashboard firstName={user.firstName} onLogout={() => logout()} />
            </div>
          ) : (
            <div className="flex flex-col items-center gap-6 px-4 py-8 text-center">
              <p className="text-[13px] leading-5 text-[#1B365E]">
                Connectez-vous pour accéder à votre espace Spot Formations.
              </p>
              <Button
                variant="gradient"
                className="w-full justify-center"
                onClick={() => {
                  onClose();
                  window.location.href = "/login";
                }}
              >
                Se connecter
              </Button>
            </div>
          )
        ) : (
          <>
            {(!config.items || config.items.length === 0) && (
              <p className="text-[13px] leading-5 text-[#1B365E] text-center px-4 py-8">
                Aucune sous-catégorie pour le moment.
                <br />
                Utilisez le bouton ci-dessous pour voir toutes les formations.
              </p>
            )}

            <ul className="subdrawer-list list-none m-0 p-0 flex flex-col gap-4.5">
              {config.items && config.items.map((item) => (
                <li key={item.label}>
                  {item.subDrawerId ? (
                    <button
                      type="button"
                      onClick={() => onOpenSubDrawer(item.subDrawerId as string)}
                      className="subdrawer-item w-full flex items-center justify-between p-[10px_16px] rounded-[28px] border border-[#EFEFEF] bg-white text-[#1B365E] font-bold md:text-sm text-xs cursor-pointer"
                    >
                      <span>{item.label}</span>
                      <span className="flex items-center justify-center w-4 h-[17.34px]" aria-hidden="true">
                        <Image className="w-[10.46px] h-[17.34px] object-contain block pointer-events-none" src={DrawerLinkArrow} width={24} height={24} alt="Ouvrir" title="Ouvrir" draggable="false" />
                      </span>
                    </button>
                  ) : (
                    <a
                      href={item.href}
                      title={item.label}
                      className="subdrawer-item flex items-center justify-between p-[10px_16px] rounded-[28px] border border-[#EFEFEF] bg-white text-[#1B365E] no-underline font-bold md:text-sm text-xs"
                    >
                      <span>{item.label}</span>
                    </a>
                  )}

                  {!item.subDrawerId && item.subItems && item.subItems.length > 0 && (
                    <ul className="subdrawer-subitems list-none m-0 mt-3 p-0 flex flex-col gap-3">
                      {item.subItems.map((sub) => (
                        <li key={sub.label}>
                          {sub.subDrawerId ? (
                            <button
                              type="button"
                              onClick={() => onOpenSubDrawer(sub.subDrawerId as string)}
                              className="subdrawer-subitem w-full flex items-center justify-between p-[10px_16px] rounded-[28px] border border-[#EFEFEF] bg-white text-[#1B365E] font-bold md:text-sm text-xs cursor-pointer"
                            >
                              <span>{sub.label}</span>
                              <span className="flex items-center justify-center w-4 h-[17.34px]" aria-hidden="true">
                                <Image className="w-[10.46px] h-[17.34px] object-contain block pointer-events-none" src={DrawerLinkArrow} width={24} height={24} alt="Ouvrir" title="Ouvrir" draggable="false" />
                              </span>
                            </button>
                          ) : (
                            <a
                              href={sub.href}
                              title={sub.label}
                              className="subdrawer-subitem flex items-center justify-between p-[10px_16px] rounded-[28px] border border-[#EFEFEF] bg-white text-[#1B365E] no-underline font-bold md:text-sm text-xs"
                            >
                              <span>{sub.label}</span>
                              <span className="flex items-center justify-center w-4 h-[17.34px]" aria-hidden="true">
                                <Image className="w-[10.46px] h-[17.34px] object-contain block pointer-events-none" src={DrawerLinkArrow} width={24} height={24} alt="Ouvrir" title="Ouvrir" draggable="false" />
                              </span>
                            </a>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>

            {config.ctas && config.ctas.length > 0 && (
              <div className="subdrawer-ctas flex flex-col gap-8 mt-auto">
                {config.ctas.map((cta, index) => {
                  // First CTA: solid gradient background
                  if (index === 0) {
                    return (
                      <Button
                        key={cta.id}
                        onClick={() => { window.location.href = cta.href; }}
                        variant="gradient"
                        className={`w-full justify-center ${cta.className || ''}`}
                      >
                        {cta.label}
                      </Button>
                    );
                  }

                  // Second CTA: gradient border, white background
                  if (index === 1) {
                    return (
                      <Button
                        key={cta.id}
                        onClick={() => { window.location.href = cta.href; }}
                        variant="outline-gradient"
                        className={`w-full justify-center text-[16px]! px-2! ${cta.className || ''}`}
                      >
                        {cta.label}
                      </Button>
                    );
                  }

                  // Fallback for any additional CTAs: keep existing behavior
                  const btnVariant: 'primary' | 'gradient' = cta.variant === 'gradient' ? 'gradient' : 'primary';
                  return (
                    <Button
                      key={cta.id}
                      onClick={() => { window.location.href = cta.href; }}
                      variant={btnVariant}
                      className={`w-full justify-center rounded-lg h-14.5 ${cta.className || ''}`}
                    >
                      {cta.label}
                    </Button>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}